import { useEffect, useRef } from 'react'
import { API_BASE_URL } from '../../../lib/axios'
import { API_ENDPOINTS } from '../../../constants/api'

export interface TaskNotification {
  type: string
  task_id: number
  title: string
  priority: string
  status: 'NEW' | 'DRAFT'
  source: string
  property_id: number
}

interface UseTaskNotificationStreamOptions {
  onNew?: (notification: TaskNotification) => void
  onDraft?: (notification: TaskNotification) => void
  enabled?: boolean
}

export function useTaskNotificationStream({
  onNew,
  onDraft,
  enabled = true,
}: UseTaskNotificationStreamOptions): void {
  const onNewRef = useRef(onNew)
  const onDraftRef = useRef(onDraft)
  onNewRef.current = onNew
  onDraftRef.current = onDraft

  useEffect(() => {
    if (!enabled) return

    let es: EventSource | null = null
    let reconnectTimeout: ReturnType<typeof setTimeout> | undefined

    function connect() {
      const token = sessionStorage.getItem('auth_token')
      if (!token) return

      const url = `${API_BASE_URL}${API_ENDPOINTS.TASK_NOTIFICATIONS_STREAM(token)}`
      es = new EventSource(url)

      es.addEventListener('task_notification', (e: MessageEvent) => {
        try {
          const n = JSON.parse(e.data as string) as TaskNotification
          if (n.status === 'NEW') onNewRef.current?.(n)
          if (n.status === 'DRAFT') onDraftRef.current?.(n)
        } catch {
          // malformed event — ignore
        }
      })

      es.onerror = () => {
        es?.close()
        es = null
        reconnectTimeout = setTimeout(connect, 3000)
      }
    }

    connect()

    return () => {
      clearTimeout(reconnectTimeout)
      es?.close()
    }
  }, [enabled])
}
